import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  BedDouble,
  Building2,
  CalendarCheck,
  Camera,
  Eye,
  EyeOff,
  Hotel,
  Loader2,
  Locate,
  MapPin,
  Pencil,
  Plus,
  Star,
  Tag,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Map, Marker } from "pigeon-maps";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { formatNaira } from "@/data/venues";
import { toast } from "sonner";
import { uploadApi, venuesApi, managerApi, resolveMediaUrl, type VenueResponse } from "@/lib/api";

export const Route = createFileRoute("/admin/listings")({
  component: ListingsPage,
});

const TYPE_LABEL: Record<string, string> = {
  Hotel: "Hotel",
  Resort: "Resort",
  Apartment: "Apartment",
  EventCentre: "Event Centre",
  OpenSpace: "Open Space",
  "Conference Hall": "Conference Hall",
  "Event Center": "Event Center",
  hotel: "Hotel",
  "event-centre": "Event Centre",
  "open-space": "Open Space",
};

const FACILITY_TYPES = [
  "Hotel",
  "Resort",
  "Apartment",
  "Conference Hall",
  "Event Center",
  "Open Space",
];

function normalizedType(type?: string | null) {
  return String(type || "").trim().toLowerCase();
}

function isEventLike(type?: string | null) {
  const clean = normalizedType(type);
  return ["eventcentre", "event centre", "event center", "event-centre", "conference hall", "open space", "open-space"].includes(clean);
}

function getPriceLabel(type?: string | null) {
  if (isEventLike(type)) return "Hire price";
  if (normalizedType(type) === "hotel") return "Starting room price";
  return "Nightly price";
}

function getPriceSuffix(type?: string | null) {
  return isEventLike(type) ? "/event" : "/night";
}

function getPriceStep(type?: string | null) {
  return isEventLike(type) ? 50000 : 5000;
}

function getInventoryLabel(type?: string | null) {
  const clean = normalizedType(type);
  if (clean === "apartment") return "Units";
  if (clean === "conference hall") return "Halls";
  if (clean === "open space" || clean === "open-space") return "Spaces";
  if (clean === "eventcentre" || clean === "event centre" || clean === "event center" || clean === "event-centre") return "Spaces";
  if (clean === "resort") return "Accommodation Types";
  return "Room Types";
}

const AMENITY_OPTIONS = [
  "WiFi",
  "Pool",
  "Parking",
  "Air conditioning",
  "Breakfast",
  "Gym",
  "Restaurant",
  "Bar",
  "Room service",
  "Pet friendly",
  "Free cancellation",
  "Conference facilities",
  "Outdoor seating",
];

interface EditVenueForm {
  name: string;
  type: string;
  location: string;
  description: string;
  basePrice: string;
  seasonalPricePercent: string;
  maxCapacity: string;
  isAvailable: boolean;
  heroImage: string;
  gallery: string[];
  highlights: string[];
  latitude: string;
  longitude: string;
}

function toEditForm(v: VenueResponse): EditVenueForm {
  return {
    name: v.name ?? "",
    type: v.type ?? "Hotel",
    location: v.location ?? "",
    description: v.description ?? "",
    basePrice: String(v.basePrice ?? ""),
    seasonalPricePercent:
      v.seasonalPrice && v.basePrice > 0
        ? String(Math.round(((v.seasonalPrice - v.basePrice) / v.basePrice) * 100))
        : "0",
    maxCapacity: v.maxCapacity != null ? String(v.maxCapacity) : "0",
    isAvailable: !!v.isAvailable,
    heroImage: v.heroImage ?? "",
    gallery: v.gallery?.map((g) => g.imageUrl).filter(Boolean) ?? [],
    highlights: v.highlights?.map((h) => h.name) ?? [],
    latitude: v.latitude != null ? String(v.latitude) : "",
    longitude: v.longitude != null ? String(v.longitude) : "",
  };
}

function VenueCard({
  v,
  onToggle,
  onDelete,
  onEdit,
}: {
  v: VenueResponse;
  onToggle: (v: VenueResponse) => void;
  onDelete: (v: VenueResponse) => void;
  onEdit: (v: VenueResponse) => void;
}) {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-border/60 bg-card text-left shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-md"
    >
      <Link
        to="/venues/$venueId"
        params={{ venueId: String(v.id) }}
        className="block transition-opacity hover:opacity-95"
        title={`View ${v.name} on the website`}
      >
      <div className="relative h-40 w-full bg-muted">
        <img src={resolveMediaUrl(v.heroImage)} alt={v.name} className="size-full object-cover" />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide shadow-sm ${
            v.isAvailable ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"
          }`}
        >
          {v.status}
        </span>
        {v.type && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
            <Tag className="size-3" /> {TYPE_LABEL[v.type] ?? v.type}
          </span>
        )}
        {v.moderationStatus && (
          <span
            className={`absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide shadow-sm ${
              v.moderationStatus === "approved"
                ? "bg-emerald-100 text-emerald-700"
                : v.moderationStatus === "rejected"
                  ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700"
            }`}
          >
            {v.moderationStatus}
          </span>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-base font-bold leading-snug truncate" title={v.name}>{v.name}</h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <MapPin className="size-3" />
            {v.location}
          </span>
          <span className="flex items-center gap-1 font-semibold text-primary">
            <Hotel className="size-3" />
            {formatNaira(v.basePrice)}
            <span className="font-normal">{getPriceSuffix(v.type)}</span>
          </span>
        </div>
        {v.description && (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
            {v.description}
          </p>
        )}
        {v.rating != null && v.rating > 0 && (
          <div className="mt-1 flex items-center gap-1 text-xs text-amber-500">
            <Star className="size-3 fill-amber-400 stroke-amber-400" />
            <span className="font-semibold">{v.rating.toFixed(1)}</span>
            <span className="text-muted-foreground">
              · {v.reviewsCount ?? 0} review{v.reviewsCount !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
      </Link>

      <div className="flex items-center gap-2 border-t border-border/60 px-4 py-3">
        <Link
          to="/admin/rooms"
          search={{ venueId: String(v.id) }}
          className="flex-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="outline" size="sm" className="w-full gap-1.5">
            <BedDouble className="size-3.5" /> {getInventoryLabel(v.type)}
          </Button>
        </Link>
        <button
          title="Edit listing"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(v);
          }}
          className="grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Pencil className="size-4" />
        </button>
        <button
          title={v.isAvailable ? "Hide listing" : "Show listing"}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(v);
          }}
          className="grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {v.isAvailable ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
        <button
          title="Delete listing"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(v);
          }}
          className="grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </div>
  );
}

function ListingsPage() {
  const { user } = useAuth();
  const [venues, setVenues] = useState<VenueResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [venueToToggle, setVenueToToggle] = useState<VenueResponse | null>(null);
  const [toggling, setToggling] = useState(false);
  const [venueToDelete, setVenueToDelete] = useState<VenueResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [venueToEdit, setVenueToEdit] = useState<VenueResponse | null>(null);
  const [editForm, setEditForm] = useState<EditVenueForm | null>(null);
  const [selectedAmenity, setSelectedAmenity] = useState("");
  const [customAmenity, setCustomAmenity] = useState("");
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [managerStats, setManagerStats] = useState<{ totalRevenue: number; totalBookings: number } | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    Promise.all([
      venuesApi.myVenues(user.id),
      managerApi.dashboard(user.id),
    ])
      .then(([venuesRes, dashRes]) => {
        const sorted = [...venuesRes.data].sort((a, b) => a.name.localeCompare(b.name));
        setVenues(sorted);
        setManagerStats({ totalRevenue: dashRes.earnings30Days, totalBookings: dashRes.bookings30Days });
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load venues"))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const totals = useMemo(
    () => ({
      avgRating: venues.reduce((s, v) => s + (v.rating ?? 0), 0) / Math.max(1, venues.length),
    }),
    [venues],
  );

  const stats = [
    { icon: Building2, label: "My Properties", value: loading ? "..." : String(venues.length) },
    {
      icon: Star,
      label: "Avg Rating",
      value: loading ? "..." : venues.length ? totals.avgRating.toFixed(1) : "-",
    },
    {
      icon: TrendingUp,
      label: "Earnings (30d)",
      value: loading ? "..." : formatNaira(managerStats?.totalRevenue ?? 0),
    },
    {
      icon: CalendarCheck,
      label: "Bookings (30d)",
      value: loading ? "..." : String(managerStats?.totalBookings ?? 0),
    },
  ];

  const handleToggle = (v: VenueResponse) => {
    setVenueToToggle(v);
  };

  const confirmToggle = async () => {
    if (!venueToToggle) return;
    const v = venueToToggle;
    const next = !v.isAvailable;
    setToggling(true);
    setVenues((vs) =>
      vs.map((x) =>
        x.id === v.id ? { ...x, isAvailable: next, status: next ? "active" : "inactive" } : x,
      ),
    );
    try {
      const updated = await venuesApi.update(v.id, { isAvailable: next });
      setVenues((vs) =>
        vs.map((x) =>
          x.id === v.id ? { ...x, isAvailable: updated.isAvailable, status: updated.status } : x,
        ),
      );
      toast.success(next ? "Listing availability turned on" : "Listing availability turned off");
    } catch (err) {
      setVenues((vs) =>
        vs.map((x) => (x.id === v.id ? { ...x, isAvailable: v.isAvailable, status: v.status } : x)),
      );
      toast.error(err instanceof Error ? err.message : "Failed to update listing");
    } finally {
      setToggling(false);
      setVenueToToggle(null);
    }
  };

  const handleDelete = (v: VenueResponse) => {
    setVenueToDelete(v);
  };

  const confirmDelete = async () => {
    if (!venueToDelete) return;
    setDeleting(true);
    try {
      const res = await venuesApi.delete(venueToDelete.id);
      if (res.deleted) {
        setVenues((vs) => vs.filter((x) => x.id !== venueToDelete.id));
        toast.success(`Successfully deleted "${venueToDelete.name}"`);
      } else {
        toast.error(`Failed to delete "${venueToDelete.name}"`);
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to delete listing. Ensure there are no active bookings or events associated with this venue.",
      );
    } finally {
      setDeleting(false);
      setVenueToDelete(null);
    }
  };

  const openEdit = (v: VenueResponse) => {
    const hasCoords = v.latitude != null && v.longitude != null;
    setVenueToEdit(v);
    setEditForm(toEditForm(v));
    setSelectedAmenity("");
    setCustomAmenity("");
    setShowMapSelector(hasCoords);
    setNewPhotoFiles([]);
    setNewPhotoPreviews([]);
    venuesApi
      .getById(v.id)
      .then((detail) => {
        const merged = { ...v, ...detail };
        setVenueToEdit(merged);
        setEditForm(toEditForm(merged));
        setShowMapSelector(merged.latitude != null && merged.longitude != null);
      })
      .catch(() => {
        // Summary data is still editable if the detail endpoint is unavailable.
      });
  };

  const updateEditForm = <K extends keyof EditVenueForm>(key: K, value: EditVenueForm[K]) => {
    setEditForm((form) => (form ? { ...form, [key]: value } : form));
  };

  const addAmenity = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setEditForm((form) =>
      form
        ? {
            ...form,
            highlights: form.highlights.includes(trimmed)
              ? form.highlights
              : [...form.highlights, trimmed],
          }
        : form,
    );
  };

  const removeAmenity = (value: string) => {
    setEditForm((form) =>
      form ? { ...form, highlights: form.highlights.filter((item) => item !== value) } : form,
    );
  };

  const pinCurrentLocation = () => {
    if (typeof window === "undefined" || !navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    toast.loading("Retrieving your location…", { id: "geo-toast" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setEditForm((form) =>
          form
            ? {
                ...form,
                latitude: String(pos.coords.latitude),
                longitude: String(pos.coords.longitude),
              }
            : form,
        );
        toast.success("Location pinned successfully!", { id: "geo-toast" });
      },
      () => {
        toast.error("Could not retrieve your location. Check browser permissions.", { id: "geo-toast" });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleNewPhotos = (files: FileList | null) => {
    if (!files || !editForm) return;
    const selected = Array.from(files);
    setNewPhotoFiles((current) => [...current, ...selected]);
    setNewPhotoPreviews((current) => [...current, ...selected.map((file) => URL.createObjectURL(file))]);
  };

  const removeExistingPhoto = (url: string) => {
    if (!editForm) return;
    if (editForm.heroImage === url) {
      updateEditForm("heroImage", editForm.gallery[0] ?? "");
      updateEditForm("gallery", editForm.gallery.slice(1));
      return;
    }
    updateEditForm(
      "gallery",
      editForm.gallery.filter((item) => item !== url),
    );
  };

  const removeNewPhoto = (index: number) => {
    URL.revokeObjectURL(newPhotoPreviews[index]);
    setNewPhotoFiles((files) => files.filter((_, i) => i !== index));
    setNewPhotoPreviews((previews) => previews.filter((_, i) => i !== index));
  };

  const makeHeroImage = (url: string) => {
    if (!editForm || editForm.heroImage === url) return;
    const previousHero = editForm.heroImage;
    const nextGallery = [previousHero, ...editForm.gallery.filter((item) => item !== url)].filter(Boolean);
    setEditForm({ ...editForm, heroImage: url, gallery: nextGallery });
  };

  const saveEdit = async () => {
    if (!venueToEdit || !editForm) return;
    if (!editForm.name.trim()) {
      toast.error("Venue name is required");
      return;
    }
    if (!editForm.location.trim()) {
      toast.error("Location is required");
      return;
    }
    const basePrice = Number(editForm.basePrice);
    if (!Number.isFinite(basePrice) || basePrice <= 0) {
      toast.error(`Enter a valid ${getPriceLabel(editForm.type).toLowerCase()}`);
      return;
    }

    setSavingEdit(true);
    try {
      let uploadedUrls: string[] = [];
      if (newPhotoFiles.length > 0) {
        const results = await Promise.allSettled(newPhotoFiles.map((file) => uploadApi.image(file)));
        uploadedUrls = results
          .filter((r): r is PromiseFulfilledResult<{ url: string }> => r.status === "fulfilled")
          .map((r) => r.value.url);
        const failed = results.length - uploadedUrls.length;
        if (failed > 0) toast.error(`${failed} photo(s) failed to upload.`);
      }

      const allImages = [editForm.heroImage, ...editForm.gallery, ...uploadedUrls].filter(Boolean);
      const heroImage = allImages[0] ?? editForm.heroImage;
      const gallery = allImages.filter((url, index) => index > 0 && url !== heroImage);
      const latitude = editForm.latitude ? Number(editForm.latitude) : undefined;
      const longitude = editForm.longitude ? Number(editForm.longitude) : undefined;
      if (
        (editForm.latitude && !Number.isFinite(latitude)) ||
        (editForm.longitude && !Number.isFinite(longitude))
      ) {
        toast.error("Enter valid latitude and longitude values");
        setSavingEdit(false);
        return;
      }

      const payload = {
        name: editForm.name.trim(),
        type: editForm.type,
        location: editForm.location.trim(),
        address: editForm.location.trim(),
        description: editForm.description.trim() || undefined,
        heroImage,
        gallery,
        basePrice,
        seasonalPrice:
          Number(editForm.seasonalPricePercent) > 0 && basePrice > 0
            ? Math.round(basePrice * (1 + Number(editForm.seasonalPricePercent) / 100))
            : undefined,
        maxCapacity: Number(editForm.maxCapacity) || 0,
        latitude,
        longitude,
        highlights: editForm.highlights
          .map((item) => item.trim())
          .filter(Boolean)
          .map((name) => ({ name })),
        isAvailable: editForm.isAvailable,
      };
      const updated = await venuesApi.update(venueToEdit.id, payload);
      const refreshed = await venuesApi.getById(venueToEdit.id).catch(() => null);
      setVenues((vs) =>
        vs
          .map((x) =>
            x.id === venueToEdit.id
              ? {
                  ...x,
                  ...(refreshed ?? {}),
                  ...payload,
                  status: updated.status ?? x.status,
                  isAvailable: updated.isAvailable ?? payload.isAvailable,
                  heroImage: refreshed?.heroImage || payload.heroImage || x.heroImage,
                  gallery: (refreshed?.gallery ?? payload.gallery.map((imageUrl, index) => ({
                    id: `${venueToEdit.id}-gallery-${index}`,
                    imageUrl,
                  }))),
                  highlights: payload.highlights,
                  latitude: refreshed?.latitude ?? payload.latitude,
                  longitude: refreshed?.longitude ?? payload.longitude,
                }
              : x,
          )
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      toast.success("Listing updated successfully");
      setVenueToEdit(null);
      setEditForm(null);
      setSelectedAmenity("");
      setCustomAmenity("");
      setNewPhotoFiles([]);
      setNewPhotoPreviews([]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update listing");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-bold">My listings</h1>
          <p className="text-sm text-muted-foreground">
            Welcome {user?.name?.split(" ")[0]}, here are your listed properties.
          </p>
        </div>
        <Link to="/list-your-property">
          <Button>
            <Plus className="mr-1 size-4" /> Add listing
          </Button>
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft"
          >
            <div className="flex items-start justify-between">
              <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary">
                <s.icon className="size-5" />
              </div>
              <ArrowUpRight className="size-3.5 text-muted-foreground/40" />
            </div>
            <div className="mt-4 font-[family-name:Inter] text-2xl font-bold">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Properties</h2>
          <Link to="/list-your-property">
            <Button variant="outline" size="sm">
              <Plus className="mr-1 size-4" /> New
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="shimmer h-64 rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-8 text-center text-sm text-destructive">
            {error}
          </div>
        ) : venues.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-16 text-center">
            <Building2 className="mb-3 size-12 text-muted-foreground/30" />
            <p className="font-semibold">No listings yet</p>
            <Link
              to="/list-your-property"
              className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Create your first listing
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {venues.map((v) => (
              <VenueCard
                key={v.id}
                v={v}
                onToggle={handleToggle}
                onDelete={handleDelete}
                onEdit={openEdit}
              />
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={venueToToggle !== null}
        onOpenChange={(open) => {
          if (!open) setVenueToToggle(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {venueToToggle?.isAvailable ? "Hide this venue?" : "Show this venue?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {venueToToggle?.isAvailable
                ? `This will hide "${venueToToggle?.name}" from public booking results until you show it again.`
                : `This will make "${venueToToggle?.name}" visible to guests again.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Cancel</AlertDialogCancel>
            <Button onClick={confirmToggle} disabled={toggling} className="gap-2">
              {toggling && <Loader2 className="size-4 animate-spin" />}
              {venueToToggle?.isAvailable ? "Hide Venue" : "Show Venue"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={venueToEdit !== null}
        onOpenChange={(open) => {
          if (!open) {
            setVenueToEdit(null);
            setEditForm(null);
            setSelectedAmenity("");
            setCustomAmenity("");
            setNewPhotoFiles([]);
            setNewPhotoPreviews([]);
          }
        }}
      >
        <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl p-4 sm:max-w-3xl sm:p-6">
          <DialogHeader>
            <DialogTitle>Edit venue information</DialogTitle>
            <DialogDescription>
              Update the core listing details guests see when browsing your venue.
            </DialogDescription>
          </DialogHeader>

          {editForm && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-3 sm:col-span-2">
                <Label>Listing photos</Label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {[editForm.heroImage, ...editForm.gallery].filter(Boolean).map((url, index) => (
                    <div
                      key={`${url}-${index}`}
                      className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-muted"
                    >
                      <img src={url} alt="Venue" className="size-full object-cover" />
                      {editForm.heroImage === url && (
                        <span className="absolute left-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                          Cover
                        </span>
                      )}
                      <div className="absolute inset-x-1 bottom-1 flex gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
                        {editForm.heroImage !== url && (
                          <button
                            type="button"
                            onClick={() => makeHeroImage(url)}
                            className="flex-1 rounded-md bg-background/90 px-2 py-1 text-[10px] font-bold text-foreground shadow"
                          >
                            Cover
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeExistingPhoto(url)}
                          className="grid size-7 place-items-center rounded-md bg-background/90 text-destructive shadow"
                          aria-label="Remove photo"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {newPhotoPreviews.map((url, index) => (
                    <div
                      key={url}
                      className="relative aspect-square overflow-hidden rounded-xl border border-dashed border-primary/40 bg-primary/5"
                    >
                      <img src={url} alt="New venue upload" className="size-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeNewPhoto(index)}
                        className="absolute right-1 top-1 grid size-7 place-items-center rounded-md bg-background/90 text-destructive shadow"
                        aria-label="Remove new photo"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  ))}
                  <label className="flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-background text-center text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary">
                    <Camera className="size-5" />
                    Add photos
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        handleNewPhotos(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="venue-name">Venue name</Label>
                <Input
                  id="venue-name"
                  value={editForm.name}
                  onChange={(e) => updateEditForm("name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-type">Facility type</Label>
                <select
                  id="venue-type"
                  title="Facility type"
                  value={editForm.type}
                  onChange={(e) => updateEditForm("type", e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {FACILITY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-location">Location</Label>
                <Input
                  id="venue-location"
                  value={editForm.location}
                  onChange={(e) => updateEditForm("location", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-base-price">{getPriceLabel(editForm.type)}</Label>
                <Input
                  id="venue-base-price"
                  type="number"
                  min={getPriceStep(editForm.type)}
                  step={getPriceStep(editForm.type)}
                  value={editForm.basePrice}
                  onChange={(e) => updateEditForm("basePrice", e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground">
                  This is shown to guests as {formatNaira(Number(editForm.basePrice) || 0)}{getPriceSuffix(editForm.type)}.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="venue-max-capacity">Max capacity (whole-venue bookings)</Label>
                <Input
                  id="venue-max-capacity"
                  type="number"
                  min={0}
                  value={editForm.maxCapacity}
                  onChange={(e) => updateEditForm("maxCapacity", e.target.value)}
                  placeholder="0 = unlimited"
                />
                <p className="text-[11px] text-muted-foreground">Set to 0 for unlimited whole-venue bookings.</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="venue-seasonal-percent">Seasonal price increase</Label>
                  <span className="text-sm font-semibold text-primary">
                    {editForm.seasonalPricePercent}%
                  </span>
                </div>
                <Slider
                  id="venue-seasonal-percent"
                  value={[Number(editForm.seasonalPricePercent) || 0]}
                  onValueChange={(val) =>
                    updateEditForm("seasonalPricePercent", String(val[0] ?? 0))
                  }
                  min={0}
                  max={100}
                  step={5}
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>0% (same as base)</span>
                  <span>
                    Computed: {" "}
                    <span className="font-semibold text-foreground">
                      {formatNaira(
                        Number(editForm.basePrice) > 0
                          ? Math.round(
                              Number(editForm.basePrice) *
                                (1 + (Number(editForm.seasonalPricePercent) || 0) / 100)
                            )
                          : 0
                      )}
                    </span>
                    {getPriceSuffix(editForm.type)} in December
                  </span>
                </div>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="venue-description">Description</Label>
                <Textarea
                  id="venue-description"
                  rows={5}
                  value={editForm.description}
                  onChange={(e) => updateEditForm("description", e.target.value)}
                />
              </div>
              <div className="space-y-3 sm:col-span-2">
                <Label htmlFor="venue-highlights">Highlights / amenities</Label>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <Select
                    value={selectedAmenity}
                    onValueChange={(value) => {
                      if (!value) return;
                      addAmenity(value);
                      setSelectedAmenity("");
                    }}
                  >
                    <SelectTrigger aria-label="Select an amenity">
                      <SelectValue placeholder="Choose from list" />
                    </SelectTrigger>
                    <SelectContent>
                      {AMENITY_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid gap-2">
                    <div className="flex gap-2">
                      <Input
                        id="venue-custom-amenity"
                        value={customAmenity}
                        onChange={(e) => setCustomAmenity(e.target.value)}
                        placeholder="Add custom amenity"
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={() => {
                          addAmenity(customAmenity);
                          setCustomAmenity("");
                        }}
                      >
                        Add
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pick from the dropdown or type a custom amenity and tap Add.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editForm.highlights.length > 0 ? (
                    editForm.highlights.map((amenity) => (
                      <span
                        key={amenity}
                        className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background px-2.5 py-1 text-sm text-foreground"
                      >
                        {amenity}
                        <button
                          type="button"
                          onClick={() => removeAmenity(amenity)}
                          className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          aria-label={`Remove ${amenity}`}
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">No amenities selected yet.</span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                {!showMapSelector ? (
                  <button
                    type="button"
                    onClick={() => setShowMapSelector(true)}
                    className="flex w-full items-start gap-3 rounded-3xl border border-border/60 bg-muted/20 p-4 text-left transition-all hover:border-primary hover:bg-muted/30"
                  >
                    <div className="size-11 grid place-items-center rounded-2xl bg-primary/10 text-primary">
                      <MapPin className="size-5" />
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="font-semibold text-foreground">Pin property location on map</p>
                      <p className="text-xs text-muted-foreground">Tap the map to set the exact listing location for guests.</p>
                    </div>
                  </button>
                ) : (
                  <div className="space-y-4 rounded-3xl border border-border/60 bg-muted/10 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <MapPin className="size-4 text-primary" />
                        <div>
                          <p className="text-sm font-semibold text-foreground">Pin property location on map</p>
                          <p className="text-xs text-muted-foreground">Click to place the marker or use your current location.</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowMapSelector(false);
                          setEditForm((form) =>
                            form
                              ? { ...form, latitude: "", longitude: "" }
                              : form,
                          );
                        }}
                        className="text-xs font-semibold text-destructive hover:underline"
                      >
                        Remove location
                      </button>
                    </div>

                    <div className="h-56 w-full overflow-hidden rounded-3xl border border-border bg-muted relative">
                      {isMounted ? (
                        <Map
                          center={
                            editForm?.latitude && editForm?.longitude
                              ? [Number(editForm.latitude), Number(editForm.longitude)]
                              : [7.3775, 3.9470]
                          }
                          zoom={editForm?.latitude && editForm?.longitude ? 14 : 8}
                          dprs={[1, 2]}
                          onClick={({ latLng }) => {
                            updateEditForm("latitude", String(latLng[0]));
                            updateEditForm("longitude", String(latLng[1]));
                          }}
                        >
                          {editForm?.latitude && editForm?.longitude && (
                            <Marker anchor={[Number(editForm.latitude), Number(editForm.longitude)]} />
                          )}
                        </Map>
                      ) : (
                        <div className="grid h-full place-items-center text-xs text-muted-foreground">Loading map…</div>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 rounded-3xl border border-border/40 bg-card p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="button"
                        onClick={pinCurrentLocation}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90"
                      >
                        <Locate className="size-4" />
                        Use current location
                      </button>
                      <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                        <div>
                          <span className="block text-[9px] uppercase tracking-wide">Latitude</span>
                          <span className="font-mono text-foreground">{editForm?.latitude || "—"}</span>
                        </div>
                        <div>
                          <span className="block text-[9px] uppercase tracking-wide">Longitude</span>
                          <span className="font-mono text-foreground">{editForm?.longitude || "—"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 rounded-xl border border-border/60 p-3 text-sm font-medium sm:col-span-2">
                <input
                  type="checkbox"
                  checked={editForm.isAvailable}
                  onChange={(e) => updateEditForm("isAvailable", e.target.checked)}
                  className="size-4 accent-primary"
                />
                Visible to guests
              </label>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setVenueToEdit(null);
                setEditForm(null);
                setSelectedAmenity("");
                setCustomAmenity("");
              }}
              disabled={savingEdit}
            >
              Cancel
            </Button>
            <Button type="button" onClick={saveEdit} disabled={savingEdit} className="gap-2">
              {savingEdit && <Loader2 className="size-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={venueToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setVenueToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the listing for{" "}
              <strong className="text-foreground">"{venueToDelete?.name}"</strong>. This action is
              blocked if there are active bookings or events associated with it, and it cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
              className="gap-2 animate-in fade-in-50 duration-200"
            >
              {deleting ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Deleting...
                </>
              ) : (
                "Delete Listing"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
